import { MouseEventHandler } from 'react';

import TAGButton, { ITAGButton } from '@shared-components-web/components/TAGButton';
import TAGSvgIcon, { IconName } from '@shared-components-web/components/TAGSvgIcon/TAGSvgIcon';
import { useAppContext } from '@shared-components-web/context/AppContextProvider.ctx';
import { useFacilityContext } from '@shared-components-web/context/FacilityContextProvider.ctx';
import { useOfficeLocationContext } from '@shared-components-web/context/OfficeLocationsContextProvider.ctx';
import { ContentfulButtonSize, ContentfulButtonVariant, Maybe } from '@shared-components-web/types';
import { replaceContentVars } from '@shared-components-web/utils/replaceContentVars/replaceContentVars';
import { resolveEntryLink } from '@shared-components-web/utils/resolveButtonEntryLink/resolveButtonEntryLink';

interface NavigationDetails {
	externalUrl?: Maybe<string>;
	openInNewTab?: Maybe<boolean>;
	openInNewWindow?: Maybe<boolean>;
	isInternal?: Maybe<boolean>;
	pageData?: {
		__typename?: Maybe<string>;
		path?: Maybe<string>;
	} | null;
}

interface ResponsiveButtonShape {
	id?: string;
	link: string;
	label?: Maybe<string>;
	variant?: Maybe<string>;
	size?: Maybe<string>;
	icon?: Maybe<string>;
	iconSize?: Maybe<string>;
	iconPosition?: Maybe<string>;
	externalUrl?: Maybe<string>;
	openInNewTab?: Maybe<boolean>;
	openInNewWindow?: Maybe<boolean>;
}

interface IContentfulButton extends Record<string, unknown> {
	label?: Maybe<string>;
	buttonLink?: Maybe<string>;
	variant?: Maybe<string>;
	size?: Maybe<string>;
	icon?: Maybe<string>;
	iconSize?: Maybe<string>;
	iconPosition?: Maybe<string>;
	navigationDetails?: NavigationDetails | null;
	mobileButton?: (Partial<IContentfulButton> & { sys?: { id?: string } }) | null;
	sys?: { id?: string };
	isSmallScreen?: boolean;
	dataTestId?: string;
	defaultVariant?: ITAGButton['variant'];
	onClick?: MouseEventHandler<HTMLButtonElement>;
	fullWidth?: boolean;
	analyticsContext?: unknown;
	ariaLabel?: string;
	docProps?: Record<string, unknown>;
}

const buttonStyles = {
	flex: '0 0 auto',
	whiteSpace: 'nowrap',
	minWidth: 'fit-content',
};

const resolveContentfulButtonVariant = (
	buttonVariant?: Maybe<string>,
	defaultVariant?: ITAGButton['variant']
): ITAGButton['variant'] => {
	switch (buttonVariant as ContentfulButtonVariant) {
		case 'Primary Default':
			return 'primaryDefault';
		case 'Primary High Contrast':
			return 'primaryHC';
		case 'Primary Accent01':
			return 'primaryAccent1';
		case 'Secondary Default':
			return 'secondaryDefault';
		case 'Secondary High Contrast':
			return 'secondaryHC';
		case 'Tertiary Default':
			return 'tertiaryDefault';
		case 'Tertiary High Contrast':
			return 'tertiaryHC';
		default:
			return defaultVariant || 'primaryDefault';
	}
};

const resolveContentfulButtonSize = (buttonSize?: Maybe<string>): ITAGButton['size'] => {
	switch (buttonSize as ContentfulButtonSize) {
		case 'extraSmall':
			return 'XS';
		case 'small':
			return 'S';
		case 'medium':
			return 'M';
		case 'large':
			return 'L';
		default:
			return 'L';
	}
};

const resolveResponsiveButton = (button: IContentfulButton, isSmallScreen: boolean): ResponsiveButtonShape => {
	const externalUrl = button.navigationDetails?.externalUrl;
	const openInNewTab = button.navigationDetails?.openInNewTab;
	const openInNewWindow = button.navigationDetails?.openInNewWindow;
	const link = button.buttonLink || button.navigationDetails?.pageData?.path || externalUrl || '';

	if (isSmallScreen && button.mobileButton) {
		return {
			id: button.mobileButton.sys?.id || button.sys?.id,
			link,
			label: button.mobileButton.label || button.label,
			variant: button.mobileButton.variant || button.variant,
			size: button.mobileButton.size || button.size,
			icon: button.mobileButton.icon || button.icon,
			iconPosition: button.mobileButton.iconPosition || button.iconPosition,
			externalUrl,
			openInNewTab,
			openInNewWindow,
			iconSize: button.iconSize,
		};
	}

	return {
		id: button.sys?.id,
		link,
		label: button.label,
		variant: button.variant,
		size: button.size,
		icon: button.icon,
		iconPosition: button.iconPosition,
		externalUrl,
		openInNewTab,
		openInNewWindow,
		iconSize: button.iconSize,
	};
};

const buttonIconSize = (size?: Maybe<string>): number => {
	const sizes: Record<string, number> = {
		XSmall: 12,
		Small: 16,
		Medium: 25,
		Large: 32,
		XLarge: 38,
	};
	return sizes[size || 'Medium'];
};

const openLinkIn = (link: string, typeOfOpening: 'same_tab' | 'new_tab' | 'new_window') => {
	if (typeOfOpening === 'new_tab') {
		window.open(link, '_blank');
	} else if (typeOfOpening === 'new_window') {
		window.open(link, '_blank', 'width=600,height=400');
	} else {
		window.location.href = link;
	}
};

const getTypeOfOpening = (
	openInNewWindow?: boolean | null,
	openInNewTab?: boolean | null
): 'new_window' | 'new_tab' | 'same_tab' => {
	if (openInNewWindow) return 'new_window';
	if (openInNewTab) return 'new_tab';
	return 'same_tab';
};

export default function ContentfulButton({
	defaultVariant,
	dataTestId = 'tag_button',
	isSmallScreen = false,
	onClick,
	fullWidth = false,
	ariaLabel,
	docProps = {},
	...buttonData
}: Readonly<IContentfulButton>) {
	const { config, stateCodeFromHeaders } = useAppContext();
	const { officeInfo } = useFacilityContext();
	const { officeInfo: officeLocationInfo } = useOfficeLocationContext();

	const button = resolveResponsiveButton(buttonData, isSmallScreen);
	const resolvedLink = resolveEntryLink({
		path: button.link,
		typeName: buttonData?.navigationDetails?.pageData?.__typename || '',
		config,
		officeInfo,
		officeLocationInfo,
	});
	const typeOfOpening = getTypeOfOpening(button.openInNewWindow, button.openInNewTab);
	const buttonLabel = replaceContentVars({
		text: button.label,
		officeInfo,
		officeLocationInfo,
		config,
	});

	const handleButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		if (button.externalUrl) {
			e.preventDefault();
			openLinkIn(button.externalUrl, typeOfOpening);
			return;
		}
		if (buttonData?.navigationDetails?.isInternal && resolvedLink) {
			e.preventDefault();
			openLinkIn(resolvedLink, typeOfOpening);
			return;
		}
		onClick?.(e);
	};

	return (
		<TAGButton
			href={button.externalUrl ? undefined : resolvedLink || undefined}
			size={resolveContentfulButtonSize(button.size)}
			variant={resolveContentfulButtonVariant(button.variant, defaultVariant)}
			startIcon={
				button.iconPosition === 'start' && button.icon ? (
					<TAGSvgIcon
						icon={button.icon as IconName}
						size={buttonIconSize(button.iconSize || 'Medium')}
						dataTestId={`${dataTestId}_start_icon_${button.id || 'button'}`}
					/>
				) : null
			}
			endIcon={
				button.iconPosition === 'end' && button.icon ? (
					<TAGSvgIcon
						icon={button.icon as IconName}
						size={buttonIconSize(button.iconSize || 'Medium')}
						dataTestId={`${dataTestId}_end_icon_${button.id || 'button'}`}
					/>
				) : null
			}
			aria-label={ariaLabel}
			analyticsContext={buttonData.analyticsContext as never}
			onClick={handleButtonClick}
			dataTestId={dataTestId}
			fullWidth={fullWidth}
			sx={{ ...buttonStyles, ...(docProps?.sx as object) }}
			data-state-code={stateCodeFromHeaders || ''}
		>
			{buttonLabel}
		</TAGButton>
	);
}
